import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import api from '../api';

const COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#22d3ee'];

// ms → "1.2s", "4m 30s", "2h 05m"
function fmtDuration(ms) {
  if (ms == null) return '—';
  if (ms < 1000) return '<1s';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${String(m % 60).padStart(2, '0')}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

// Date → value for <input type="datetime-local"> (local time, minute precision)
function toLocalInput(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

const PRESETS = [
  { label: 'Today',   hours: null },
  { label: '24h',     hours: 24 },
  { label: '7 days',  hours: 24 * 7 },
  { label: '30 days', hours: 24 * 30 },
];

export default function Analytics() {
  const [overview, setOverview] = useState(null);
  const [broadcasts, setBroadcasts] = useState([]);
  const [agentData, setAgentData] = useState(null);
  const [preset, setPreset] = useState('7 days');
  const [from, setFrom] = useState(() => toLocalInput(new Date(Date.now() - 7 * 24 * 3600 * 1000)));
  const [to, setTo] = useState(() => toLocalInput(new Date()));

  useEffect(() => {
    api.get('/analytics/overview').then(r => setOverview(r.data));
    api.get('/analytics/broadcasts').then(r => setBroadcasts(r.data));
  }, []);

  useEffect(() => {
    const fromD = new Date(from), toD = new Date(to);
    if (isNaN(fromD) || isNaN(toD) || fromD > toD) return;
    api.get('/analytics/agents', { params: { from: fromD.toISOString(), to: toD.toISOString() } })
      .then(r => setAgentData(r.data))
      .catch(() => setAgentData(null));
  }, [from, to]);

  const applyPreset = (p) => {
    setPreset(p.label);
    const now = new Date();
    if (p.hours === null) {
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);
      setFrom(toLocalInput(start));
    } else {
      setFrom(toLocalInput(new Date(now.getTime() - p.hours * 3600 * 1000)));
    }
    setTo(toLocalInput(now));
  };

  const funnelData = overview ? [
    { name: 'Sent', value: overview.total_sent || 0 },
    { name: 'Delivered', value: overview.total_delivered || 0 },
    { name: 'Read', value: overview.total_read || 0 },
    { name: 'Clicked', value: overview.total_clicked || 0 },
  ] : [];

  const optData = overview ? [
    { name: 'Opted In', value: overview.opted_in || 0 },
    { name: 'Opted Out', value: overview.opted_out || 0 },
  ] : [];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Analytics</h1>
      </div>

      <div className="stat-grid" style={{ marginBottom: 24 }}>
        <StatCard label="Total Contacts" value={overview?.total_contacts ?? '—'} />
        <StatCard label="Delivery Rate" value={`${overview?.delivery_rate ?? 0}%`} color="var(--success)" />
        <StatCard label="Read Rate" value={`${overview?.open_rate ?? 0}%`} color="var(--warning)" />
        <StatCard label="CTR" value={`${overview?.ctr ?? 0}%`} color="#22d3ee" />
        <StatCard label="Total Clicks" value={overview?.total_clicks ?? 0} color="var(--primary-light)" />
        <StatCard label="Broadcasts" value={overview?.total_broadcasts ?? 0} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20, marginBottom: 24 }}>
        <div className="card">
          <div className="card-title">Message Funnel (all time)</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={funnelData} barSize={48}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 12 }} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} />
              <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {funnelData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <div className="card-title">Opt-in Status</div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={optData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} dataKey="value" paddingAngle={3}>
                {optData.map((_, i) => <Cell key={i} fill={i === 0 ? '#22c55e' : '#ef4444'} />)}
              </Pie>
              <Legend formatter={v => <span style={{ color: '#94a3b8', fontSize: 12 }}>{v}</span>} />
              <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div className="card-title" style={{ marginBottom: 0, marginRight: 'auto' }}>Agent Performance</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {PRESETS.map(p => (
              <button
                key={p.label}
                className={`btn ${preset === p.label ? 'btn-primary' : ''}`}
                style={{ padding: '5px 10px', fontSize: 12 }}
                onClick={() => applyPreset(p)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="datetime-local" className="input" value={from}
              style={{ width: 'auto', padding: '5px 8px', fontSize: 12 }}
              onChange={e => { setFrom(e.target.value); setPreset(null); }}
            />
            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>to</span>
            <input
              type="datetime-local" className="input" value={to}
              style={{ width: 'auto', padding: '5px 8px', fontSize: 12 }}
              onChange={e => { setTo(e.target.value); setPreset(null); }}
            />
          </div>
        </div>

        <div className="stat-grid" style={{ marginBottom: 20 }}>
          <StatCard label="Messages Sent" value={agentData?.overall.messages_sent ?? '—'} />
          <StatCard label="Conversations Handled" value={agentData?.overall.conversations_handled ?? '—'} color="#22d3ee" />
          <StatCard label="Avg First Response" value={fmtDuration(agentData?.overall.avg_response_ms)} color="var(--warning)" />
          <StatCard label="Fastest Response" value={fmtDuration(agentData?.overall.fastest_response_ms)} color="var(--success)" />
          <StatCard label="Resolved" value={agentData?.overall.resolved ?? '—'} color="var(--success)" />
          <StatCard
            label="Active / Total Agents"
            value={agentData ? `${agentData.overall.agents_active}/${agentData.overall.agents_total}` : '—'}
            color="var(--primary-light)"
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20, marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.5, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>
              Time Stats
            </div>
            <StatRow
              label="Avg. Agent First Response (without queue time)"
              value={fmtDuration(agentData?.time_stats?.avg_agent_response_ms)}
            />
            <StatRow label="Avg. Queue Time" value={fmtDuration(agentData?.time_stats?.avg_queue_ms)} />
            <StatRow label="Avg. First Response Time (incl. queue)" value={fmtDuration(agentData?.time_stats?.avg_first_response_ms)} />
            <StatRow label="Avg. Resolution Time" value={fmtDuration(agentData?.time_stats?.avg_resolution_ms)} />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.5, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>
              Chat Stats
            </div>
            <StatRow label="Chats Received" value={agentData?.chat_stats?.chats_received ?? '—'} />
            <StatRow
              label="Closed Chats"
              value={agentData ? `${agentData.chat_stats.closed} (${agentData.chat_stats.closed_pct}%)` : '—'}
            />
            <StatRow
              label="Chats Abandoned by Users"
              value={agentData ? `${agentData.chat_stats.abandoned} (${agentData.chat_stats.abandoned_pct}%)` : '—'}
            />
            <StatRow
              label={`Delayed Chats (first reply > ${agentData?.chat_stats?.delayed_threshold_min ?? 5}m)`}
              value={agentData ? `${agentData.chat_stats.delayed} (${agentData.chat_stats.delayed_pct}%)` : '—'}
            />
            <StatRow
              label="Bot Replies (chats handled by bot)"
              value={agentData ? `${agentData.chat_stats.bot_replies} (${agentData.chat_stats.bot_conversations} chats)` : '—'}
            />
          </div>
        </div>

        {agentData && agentData.agents.some(a => a.messages_sent > 0 || a.resolved > 0) && (
          <div style={{ marginBottom: 20 }}>
            <ResponsiveContainer width="100%" height={Math.max(agentData.agents.length * 44 + 40, 120)}>
              <BarChart
                layout="vertical" barSize={14}
                data={agentData.agents.map(a => ({
                  name: a.agent.name || a.agent.email || 'Unknown',
                  Messages: a.messages_sent,
                  Resolved: a.resolved,
                }))}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                <YAxis type="category" dataKey="name" width={120} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} />
                <Legend formatter={v => <span style={{ color: '#94a3b8', fontSize: 12 }}>{v}</span>} />
                <Bar dataKey="Messages" fill="#6366f1" radius={[0, 4, 4, 0]} />
                <Bar dataKey="Resolved" fill="#22c55e" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {!agentData || agentData.agents.length === 0 ? (
          <div className="empty-state" style={{ padding: 40 }}>No agent activity in this period</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Agent</th><th>Messages</th><th>Conversations</th>
                  <th>Avg First Response</th><th>Fastest</th>
                  <th>Resolved</th><th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {agentData.agents.map(a => (
                  <tr key={a.agent._id}>
                    <td style={{ fontWeight: 500 }}>
                      {a.agent.name || a.agent.email || 'Unknown'}
                      {a.agent.team_role !== 'agent' && (
                        <span className="badge badge-purple" style={{ marginLeft: 8 }}>Owner</span>
                      )}
                      {a.agent.team_role === 'agent' && !a.agent.isActive && (
                        <span className="badge" style={{ marginLeft: 8 }}>Inactive</span>
                      )}
                    </td>
                    <td>{a.messages_sent}</td>
                    <td>{a.conversations_handled}</td>
                    <td>{fmtDuration(a.avg_response_ms)}</td>
                    <td>{fmtDuration(a.fastest_response_ms)}</td>
                    <td>
                      <span className={`badge ${a.resolved > 0 ? 'badge-green' : ''}`}>{a.resolved}</span>
                    </td>
                    <td>{a.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-title">Broadcast Performance</div>
        {broadcasts.length === 0 ? (
          <div className="empty-state" style={{ padding: 40 }}>No sent broadcasts yet</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th><th>Channel</th><th>Recipients</th>
                  <th>Sent</th><th>Delivered</th><th>Read</th><th>Clicked</th>
                  <th>Read Rate</th><th>CTR</th><th>Date</th>
                </tr>
              </thead>
              <tbody>
                {broadcasts.map(b => {
                  const s = b.stats || {};
                  const readRate = s.delivered ? ((s.read / s.delivered) * 100).toFixed(1) : '—';
                  const ctr = s.delivered ? ((s.clicked / s.delivered) * 100).toFixed(1) : '—';
                  return (
                    <tr key={b._id}>
                      <td style={{ fontWeight: 500 }}>{b.name}</td>
                      <td><span style={{ fontSize: 16 }}>{b.channel === 'whatsapp' ? '📱' : '📧'}</span></td>
                      <td>{b.total_count}</td>
                      <td>{s.sent ?? 0}</td>
                      <td>{s.delivered ?? 0}</td>
                      <td>{s.read ?? 0}</td>
                      <td>{s.clicked ?? 0}</td>
                      <td><span className={`badge ${readRate > 30 ? 'badge-green' : 'badge-yellow'}`}>{readRate}%</span></td>
                      <td><span className="badge badge-blue">{ctr}%</span></td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{b.sent_at ? new Date(b.sent_at).toLocaleDateString() : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatRow({ label, value }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
      padding: '10px 0', borderBottom: '1px solid var(--border, #334155)',
    }}>
      <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap' }}>{value}</span>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="stat-card">
      <div className="stat-value" style={color ? { color } : {}}>{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}
